export const BREADBOARD_TEMPLATE_TYPE = 'breadboard';

export const BREADBOARD_TERMINAL_COLUMN_COUNT = 63;

const TERMINAL_LOWER_ROWS = Object.freeze(['A', 'B', 'C', 'D', 'E']);
const TERMINAL_UPPER_ROWS = Object.freeze(['F', 'G', 'H', 'I', 'J']);
const RAIL_ROWS = Object.freeze(['W', 'X', 'Y', 'Z']);
const RAIL_ROW_LABELS = Object.freeze({
  W: 'Bottom red power rail',
  X: 'Bottom blue ground rail',
  Y: 'Top red power rail',
  Z: 'Top blue ground rail',
});

export const BREADBOARD_RAIL_COLUMNS = Object.freeze(
  Array.from({ length: 10 }).flatMap((_, groupIndex) => {
    const start = 3 + groupIndex * 6;
    return Array.from({ length: 5 }, (_unused, offset) => start + offset);
  })
);

export const BREADBOARD_PIN_KEYS = Object.freeze([
  ...Array.from({ length: BREADBOARD_TERMINAL_COLUMN_COUNT }).flatMap((_unused, index) => {
    const column = index + 1;
    return [...TERMINAL_LOWER_ROWS, ...TERMINAL_UPPER_ROWS].map((row) => `pin${column}${row}`);
  }),
  ...BREADBOARD_RAIL_COLUMNS.flatMap((column) => (
    RAIL_ROWS.map((row) => `pin${column}${row}`)
  )),
]);

const BREADBOARD_PIN_KEY_SET = new Set(BREADBOARD_PIN_KEYS);

export const stripBreadboardMarkerIndex = (value) => (
  String(value || '').replace(/__\d+$/, '')
);

export const getBreadboardMarkerKeysForPinKey = (pinKey, markerKeys = []) => {
  const logicalPinKey = stripBreadboardMarkerIndex(pinKey);

  if (!logicalPinKey) {
    return [];
  }

  return (Array.isArray(markerKeys) ? markerKeys : [])
    .map((markerKey) => String(markerKey || '').trim())
    .filter((markerKey) => stripBreadboardMarkerIndex(markerKey) === logicalPinKey);
};

export const isBreadboardComponent = (component) => (
  component?.type === 'passive' &&
  String(component?.passiveType || component?.templateType || '') === BREADBOARD_TEMPLATE_TYPE
);

export const getBreadboardPinInfo = (value) => {
  const pinKey = stripBreadboardMarkerIndex(value);
  const match = pinKey.match(/^pin(\d+)([A-Z])$/i);

  if (!match) {
    return null;
  }

  const column = Number(match[1]);
  const row = match[2].toUpperCase();

  if (!Number.isInteger(column) || column < 1) {
    return null;
  }

  if (!BREADBOARD_PIN_KEY_SET.has(`pin${column}${row}`)) {
    return null;
  }

  if (TERMINAL_LOWER_ROWS.includes(row)) {
    return {
      pinKey: `pin${column}${row}`,
      column,
      row,
      busKey: `terminal:${column}:A-E`,
      busLabel: `Column ${column} A-E`,
      rowGroup: 'terminal-lower',
    };
  }

  if (TERMINAL_UPPER_ROWS.includes(row)) {
    return {
      pinKey: `pin${column}${row}`,
      column,
      row,
      busKey: `terminal:${column}:F-J`,
      busLabel: `Column ${column} F-J`,
      rowGroup: 'terminal-upper',
    };
  }

  if (RAIL_ROWS.includes(row)) {
    return {
      pinKey: `pin${column}${row}`,
      column,
      row,
      busKey: `rail:${row}`,
      busLabel: RAIL_ROW_LABELS[row] || `Rail ${row}`,
      rowGroup: 'rail',
    };
  }

  return null;
};

export const getBreadboardBusKeyForPinKey = (pinKey) => (
  getBreadboardPinInfo(pinKey)?.busKey || ''
);

export const getBreadboardDisplayNameForPinKey = (pinKey) => {
  const info = getBreadboardPinInfo(pinKey);

  if (!info) {
    return stripBreadboardMarkerIndex(pinKey) || 'Breadboard hole';
  }

  if (info.rowGroup === 'rail') {
    return `${info.busLabel} hole ${info.column}`;
  }

  return `${info.busLabel} hole ${info.row}`;
};

export const getBreadboardConnectedPinKeys = (pinKey) => {
  const info = getBreadboardPinInfo(pinKey);

  if (!info) {
    return [];
  }

  if (info.rowGroup === 'terminal-lower') {
    return TERMINAL_LOWER_ROWS.map((row) => `pin${info.column}${row}`);
  }

  if (info.rowGroup === 'terminal-upper') {
    return TERMINAL_UPPER_ROWS.map((row) => `pin${info.column}${row}`);
  }

  return BREADBOARD_RAIL_COLUMNS.map((column) => `pin${column}${info.row}`);
};

export const getBreadboardOppositeTerminalPinKeys = (pinKey) => {
  const info = getBreadboardPinInfo(pinKey);

  if (!info || !['terminal-lower', 'terminal-upper'].includes(info.rowGroup)) {
    return [];
  }

  const oppositeRows = info.rowGroup === 'terminal-lower'
    ? TERMINAL_UPPER_ROWS
    : TERMINAL_LOWER_ROWS;

  return oppositeRows.map((row) => `pin${info.column}${row}`);
};

export const getBreadboardPlacementMatches = ({
  componentStart,
  pinOffsets = {},
  targets = [],
  dx = 0,
  dy = 0,
  contactRadius = 3,
} = {}) => {
  if (
    !componentStart ||
    !Number.isFinite(componentStart.x) ||
    !Number.isFinite(componentStart.y)
  ) {
    return [];
  }

  const safeContactRadius = Math.max(0, Number(contactRadius) || 0);
  const contactRadiusSquared = safeContactRadius * safeContactRadius;

  return Object.entries(pinOffsets).map(([componentPinKey, offset]) => {
    if (!offset || !Number.isFinite(offset.x) || !Number.isFinite(offset.y)) {
      return null;
    }

    const componentPinPoint = {
      x: componentStart.x + dx + offset.x,
      y: componentStart.y + dy + offset.y,
    };
    let bestMatch = null;

    targets.forEach((target) => {
      if (
        !target?.point ||
        !Number.isFinite(target.point.x) ||
        !Number.isFinite(target.point.y)
      ) {
        return;
      }

      const deltaX = target.point.x - componentPinPoint.x;
      const deltaY = target.point.y - componentPinPoint.y;
      const distanceSquared = (deltaX * deltaX) + (deltaY * deltaY);

      if (
        distanceSquared <= contactRadiusSquared &&
        (!bestMatch || distanceSquared < bestMatch.distanceSquared)
      ) {
        bestMatch = {
          componentPinKey,
          targetPartId: target.partId,
          targetPinKey: target.pinKey,
          targetPoint: target.point,
          distanceSquared,
        };
      }
    });

    return bestMatch;
  }).filter(Boolean);
};

export const getBreadboardConnectedMarkerKeys = (pinKey, markerKeys = []) => {
  const connectedBaseKeys = new Set(getBreadboardConnectedPinKeys(pinKey));

  if (connectedBaseKeys.size === 0) {
    return [];
  }

  return (Array.isArray(markerKeys) ? markerKeys : [])
    .map((markerKey) => String(markerKey || '').trim())
    .filter((markerKey) => connectedBaseKeys.has(stripBreadboardMarkerIndex(markerKey)));
};

export const getBreadboardBusGroupsFromMarkerKeys = (markerKeys = []) => {
  const groupsByBusKey = new Map();

  (Array.isArray(markerKeys) ? markerKeys : []).forEach((markerKey) => {
    const safeMarkerKey = String(markerKey || '').trim();
    const busKey = getBreadboardBusKeyForPinKey(safeMarkerKey);

    if (!safeMarkerKey || !busKey) {
      return;
    }

    if (!groupsByBusKey.has(busKey)) {
      groupsByBusKey.set(busKey, []);
    }

    groupsByBusKey.get(busKey).push(safeMarkerKey);
  });

  return Array.from(groupsByBusKey.values())
    .filter((group) => group.length > 1);
};
